import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, MapPin, ShieldCheck } from "lucide-react";
import { api, type CheckinPreview, type SavedPlace } from "../lib/api";
import { distanceMeters } from "../lib/geo";

type Props = { enabled: boolean };
type Found = { place: SavedPlace; position: GeolocationPosition };

const NEARBY_METERS = 250;
const MAX_ACCURACY_METERS = 200;
const RECHECK_MS = 5 * 60_000;
const SNOOZE_MS = 12 * 3600_000;
const snoozeKey = (id: string) => `travel:nearby-snooze:${id}`;

function snoozed(id: string) {
  try { return Date.now() < Number(localStorage.getItem(snoozeKey(id)) ?? 0); } catch { return false; }
}
function snooze(id: string) {
  try { localStorage.setItem(snoozeKey(id), String(Date.now() + SNOOZE_MS)); } catch { /* storage non disponibile */ }
}

// Se il GPS mostra che il dispositivo è vicino a un luogo salvato mai visitato, propone il check-in.
// Non chiede mai il permesso da solo: parte solo se la posizione è già stata concessa a Travel.
export function NearbyCheckin({ enabled }: Props) {
  const [found, setFound] = useState<Found>();
  const [preview, setPreview] = useState<CheckinPreview>();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const lastCheck = useRef(0);
  const running = useRef(false);

  const look = useCallback(async () => {
    if (running.current || Date.now() - lastCheck.current < RECHECK_MS || !navigator.geolocation || !navigator.permissions) return;
    running.current = true;
    try {
      const status = await navigator.permissions.query({ name: "geolocation" });
      if (status.state !== "granted") return;
      lastCheck.current = Date.now();
      const [position, { places }] = await Promise.all([
        new Promise<GeolocationPosition>((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: false, timeout: 10_000, maximumAge: 60_000 })),
        api.savedPlaces(),
      ]);
      if (position.coords.accuracy > MAX_ACCURACY_METERS) return;
      const here = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      const nearest = places
        .filter((place) => !place.visitCount && !snoozed(place.id))
        .map((place) => ({ place, distance: distanceMeters(here, place) }))
        .filter((item) => item.distance <= NEARBY_METERS)
        .sort((a, b) => a.distance - b.distance)[0];
      if (nearest) setFound({ place: nearest.place, position });
    } catch { /* posizione o elenco non disponibili: nessuna proposta */ }
    finally { running.current = false; }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void look();
    const onVisible = () => { if (document.visibilityState === "visible") void look(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, look]);

  async function propose() {
    if (!found) return;
    setBusy(true); setMessage(undefined);
    try { setPreview(await api.checkinPreview(found.place.id, found.position)); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Check-in non riuscito"); }
    finally { setBusy(false); }
  }

  async function confirm() {
    if (!preview || !found) return;
    setBusy(true);
    try { await api.confirmCheckin(preview.token); setPreview(undefined); setFound(undefined); }
    catch (error) { setMessage(error instanceof Error ? error.message : "Check-in non riuscito"); }
    finally { setBusy(false); }
  }

  function dismiss() {
    if (found) snooze(found.place.id);
    setFound(undefined); setPreview(undefined); setMessage(undefined);
  }

  if (!found) return null;
  return createPortal(<div className="modal-layer"><section className="checkin-dialog" role="dialog" aria-modal="true" aria-labelledby="nearby-title">
    <span className={preview && !preview.nearby ? "confirm-symbol is-warning" : "confirm-symbol"}>{preview ? (preview.nearby ? <ShieldCheck /> : <AlertTriangle />) : <MapPin />}</span>
    <span className="eyebrow">{preview ? "Conferma richiesta" : "Luogo nuovo vicino a te"}</span>
    {preview
      ? <><h2 id="nearby-title">Check-in a {preview.placeName}?</h2><p>Il dispositivo risulta a <strong>{formatDistance(preview.distanceMeters)}</strong> dal luogo, con precisione di circa ±{preview.accuracy} m. La posizione non è ancora stata salvata.</p></>
      : <><h2 id="nearby-title">Sei a {found.place.name}</h2><p>Il GPS mostra che sei qui e non risulta nessuna visita precedente. Vuoi registrare il check-in?</p></>}
    {message && <p className="distance-warning">{message}</p>}
    <div className="dialog-actions"><button onClick={dismiss}>{preview ? "Annulla" : "Non ora"}</button><button onClick={() => void (preview ? confirm() : propose())} disabled={busy}>{preview ? "Conferma check-in" : "Fai check-in"}</button></div>
  </section></div>, document.body);
}

function formatDistance(meters: number) { return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`; }
